/**
 * Dropped item entities — broken blocks drop a small spinning,
 * bobbing cube at the block position; walking near it picks it up
 * automatically (Minecraft style).
 */

import * as THREE from "three";
import { BlockType, isBlockSolid, type WorldBlockAccess } from "@craftgaming/shared";
import { getSlotIcon } from "../ui/hotbar";

const PICKUP_RADIUS = 1.4;
const PICKUP_DELAY = 0.4; // seconds before it can be picked up
const DESPAWN_TIME = 90; // seconds
const MAX_DROPS = 120;
const GRAVITY = 14;
const CUBE_SIZE = 0.28;

type Drop = {
  mesh: THREE.Mesh;
  itemId: number;
  count: number;
  vy: number;
  age: number;
  baseY: number;
  grounded: boolean;
};

export type PickedItem = { itemId: number; count: number };

export type DroppedItems = {
  spawn: (wx: number, wy: number, wz: number, itemId: number, count: number) => void;
  /**
   * Advances physics/animation; returns items picked up by the player.
   * Drops that don't pass `canPickup` stay on the ground (full inventory
   * must never destroy items).
   */
  update: (
    dt: number,
    playerX: number,
    playerY: number,
    playerZ: number,
    canPickup: (itemId: number, count: number) => boolean
  ) => PickedItem[];
  dispose: () => void;
};

export function createDroppedItems(
  scene: THREE.Scene,
  worldAccess: WorldBlockAccess
): DroppedItems {
  const drops: Drop[] = [];
  const geometry = new THREE.BoxGeometry(CUBE_SIZE, CUBE_SIZE, CUBE_SIZE);
  const textureLoader = new THREE.TextureLoader();
  const materialCache = new Map<number, THREE.MeshLambertMaterial>();

  function materialFor(itemId: number): THREE.MeshLambertMaterial {
    let material = materialCache.get(itemId);
    if (!material) {
      const texture = textureLoader.load(getSlotIcon(itemId, 32));
      texture.magFilter = THREE.NearestFilter;
      texture.colorSpace = THREE.SRGBColorSpace;
      material = new THREE.MeshLambertMaterial({ map: texture });
      materialCache.set(itemId, material);
    }
    return material;
  }

  function spawn(wx: number, wy: number, wz: number, itemId: number, count: number) {
    if (drops.length >= MAX_DROPS) {
      // Drop the oldest to make room — never silently refuse new drops
      const oldest = drops.shift();
      if (oldest) scene.remove(oldest.mesh);
    }

    const mesh = new THREE.Mesh(geometry, materialFor(itemId));
    mesh.position.set(wx + 0.5, wy + 0.4, wz + 0.5);
    mesh.rotation.y = Math.random() * Math.PI;
    scene.add(mesh);

    drops.push({
      mesh,
      itemId,
      count,
      vy: 1.5,
      age: 0,
      baseY: wy + 0.3,
      grounded: false,
    });
  }

  function update(
    dt: number,
    playerX: number,
    playerY: number,
    playerZ: number,
    canPickup: (itemId: number, count: number) => boolean
  ): PickedItem[] {
    const picked: PickedItem[] = [];

    for (let i = drops.length - 1; i >= 0; i--) {
      const drop = drops[i];
      drop.age += dt;

      if (drop.age > DESPAWN_TIME) {
        scene.remove(drop.mesh);
        drops.splice(i, 1);
        continue;
      }

      // Simple vertical physics until it rests on a solid block
      if (!drop.grounded) {
        drop.vy -= GRAVITY * dt;
        let newY = drop.mesh.position.y + drop.vy * dt;
        const blockBelow = worldAccess.getBlockAtWorld(
          Math.floor(drop.mesh.position.x),
          Math.floor(newY - CUBE_SIZE / 2),
          Math.floor(drop.mesh.position.z)
        );
        if (isBlockSolid(blockBelow) || blockBelow === BlockType.BEDROCK) {
          newY = Math.floor(newY - CUBE_SIZE / 2) + 1 + CUBE_SIZE / 2;
          drop.grounded = true;
          drop.baseY = newY;
          drop.vy = 0;
        } else if (newY < 1) {
          newY = 1;
          drop.grounded = true;
          drop.baseY = 1;
        }
        drop.mesh.position.y = newY;
      } else {
        // Re-check support — the block under a resting drop may be broken
        const support = worldAccess.getBlockAtWorld(
          Math.floor(drop.mesh.position.x),
          Math.floor(drop.baseY - CUBE_SIZE / 2 - 0.05),
          Math.floor(drop.mesh.position.z)
        );
        if (!isBlockSolid(support)) {
          drop.grounded = false;
          drop.vy = 0;
        } else {
          // Spin + bob (Minecraft item feel)
          drop.mesh.position.y = drop.baseY + Math.sin(drop.age * 2.5) * 0.07 + 0.05;
        }
      }
      drop.mesh.rotation.y += dt * 1.8;

      // Auto pickup by proximity — only when the inventory can hold it
      if (drop.age >= PICKUP_DELAY) {
        const dx = drop.mesh.position.x - playerX;
        const dy = drop.mesh.position.y - (playerY + 0.9);
        const dz = drop.mesh.position.z - playerZ;
        if (
          dx * dx + dy * dy + dz * dz < PICKUP_RADIUS * PICKUP_RADIUS &&
          canPickup(drop.itemId, drop.count)
        ) {
          picked.push({ itemId: drop.itemId, count: drop.count });
          scene.remove(drop.mesh);
          drops.splice(i, 1);
        }
      }
    }

    return picked;
  }

  function dispose() {
    for (const drop of drops) scene.remove(drop.mesh);
    drops.length = 0;
    geometry.dispose();
    for (const material of materialCache.values()) {
      material.map?.dispose();
      material.dispose();
    }
    materialCache.clear();
  }

  return { spawn, update, dispose };
}
